import Button, { ButtonProps } from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  createStyles,
  Theme,
  WithStyles,
  withStyles
} from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import { DEFAULT_NOTIFICATION_SHOW_TIME } from "@saleor/config";
import { buttonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

export type ConfirmButtonTransitionState =
  | "loading"
  | "success"
  | "error"
  | "default";

const styles = (theme: Theme) =>
  createStyles({
    adornmentKeepLabel: {
      position: "initial",
      transition: theme.transitions.create(["height", "width"], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut
      })
    },
    btn: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      transition: theme.transitions.create(["padding"], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut
      }),
      ...{
        "& .MuiButton-label": {
          gap: 0,
          transition: theme.transitions.create(["gap"], {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut
          })
        }
      }
    },
    btnKeepLabel: {
      paddingLeft: theme.spacing(4.5),
      paddingRight: theme.spacing(4.5)
    },
    btnWithAdornment: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      ...{
        "& .MuiButton-label": {
          gap: "16px"
        }
      }
    },
    error: {
      "&:hover": {
        backgroundColor: theme.palette.error.main
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    icon: {
      marginLeft: "0 !important",
      transitionDuration: theme.transitions.duration.standard + "ms"
    },
    iconAbsolute: {
      position: "absolute"
    },
    invisible: {
      opacity: 0
    },
    label: {
      alignItems: "center",
      color: "#ffffff",
      display: "flex",
      transitionDuration: theme.transitions.duration.standard + "ms"
    },
    progress: {
      "& svg": {
        color: theme.palette.common.white,
        margin: 0
      },
      transitionDuration: theme.transitions.duration.standard + "ms"
    },
    progressAbsolute: {
      position: "absolute"
    },
    success: {
      "&:hover": {
        backgroundColor: theme.palette.primary.main
      },
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  });

export interface ConfirmButtonProps
  extends Omit<ButtonProps, "classes">,
    WithStyles<typeof styles> {
  keepLabel?: boolean;
  idConfirmButton?: string;
  transitionState: ConfirmButtonTransitionState;
  onTransitionToDefault?: () => void;
}

interface ConfirmButtonState {
  displayCompletedActionState: boolean;
}

const ConfirmButton = withStyles(styles, { name: "ConfirmButton" })(
  class ConfirmButtonComponent extends React.Component<
    ConfirmButtonProps &
      WithStyles<
        "error" | "icon" | "invisible" | "label" | "progress" | "success"
      >,
    ConfirmButtonState
  > {
    state: ConfirmButtonState = {
      displayCompletedActionState: false
    };
    timeout = null;

    static getDerivedStateFromProps(
      nextProps: ConfirmButtonProps,
      prevState: ConfirmButtonState
    ): ConfirmButtonState {
      if (nextProps.transitionState === "loading") {
        return {
          displayCompletedActionState: true
        };
      }
      return prevState;
    }

    componentDidUpdate(prevProps: ConfirmButtonProps) {
      const { transitionState, onTransitionToDefault } = this.props;
      if (prevProps.transitionState !== transitionState) {
        if (
          (["error", "success"] as ConfirmButtonTransitionState[]).includes(
            transitionState
          )
        ) {
          this.timeout = setTimeout(() => {
            this.setState({
              displayCompletedActionState: false
            });
            if (onTransitionToDefault) {
              onTransitionToDefault();
            }
          }, DEFAULT_NOTIFICATION_SHOW_TIME);
        } else if (transitionState === "loading") {
          clearTimeout(this.timeout);
        }
      }
    }

    componentWillUnmount() {
      clearTimeout(this.timeout);
    }

    render() {
      const {
        children,
        idConfirmButton,
        classes,
        className,
        disabled,
        fullWidth,
        keepLabel,
        transitionState,
        onClick,
        onTransitionToDefault: _,
        ...props
      } = this.props;
      const { displayCompletedActionState } = this.state;

      return (
        <Button
          id={idConfirmButton ? `${idConfirmButton}` : "saveBtn"}
          variant="contained"
          onClick={transitionState === "loading" ? undefined : onClick}
          color="primary"
          className={classNames(classes.btn, {
            [classes.error]:
              transitionState === "error" && displayCompletedActionState,
            [classes.success]:
              transitionState === "success" && displayCompletedActionState,
            [className]: true
          })}
          disabled={disabled}
          {...props}
        >
          <span
            className={classNames({
              [classes.label]: true,
              [classes.invisible]:
                (transitionState === "loading" ||
                  transitionState === "success") &&
                displayCompletedActionState &&
                !keepLabel
            })}
          >
            {transitionState === "error" && displayCompletedActionState ? (
              <FormattedMessage defaultMessage="Error" description="button" />
            ) : (
              children || <FormattedMessage {...buttonMessages.confirm} />
            )}
          </span>

          {(!keepLabel || transitionState !== "success") && (
            <CircularProgress
              size={!keepLabel ? 24 : transitionState === "loading" ? 24 : 0}
              color="inherit"
              className={classNames({
                [classes.adornmentKeepLabel]: keepLabel,
                [classes.progress]: true,
                [classes.progressAbsolute]: !keepLabel,
                [classes.invisible]: transitionState !== "loading"
              })}
            />
          )}

          {(!keepLabel || transitionState === "success") && (
            <CheckIcon
              className={classNames({
                [classes.adornmentKeepLabel]: keepLabel,
                [classes.icon]: true,
                [classes.iconAbsolute]: !keepLabel,
                [classes.invisible]: !(
                  transitionState === "success" && displayCompletedActionState
                )
              })}
            />
          )}
        </Button>
      );
    }
  }
);

ConfirmButton.defaultProps = {
  keepLabel: false
};

ConfirmButton.displayName = "ConfirmButton";

export default ConfirmButton;
